import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, X, ShoppingBag, ChevronRight, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr, fmtQty, UNITS } from "./lib";

const unitLabel = (k) => (UNITS.find((u) => u.key === k) || { label: k || "" }).label;

/**
 * Counter Sales — walk-in retail sale flow.
 * List view + inline "New Sale" dialog with part picker, live totals and
 * one-shot submit with payment mode inline.
 */
export default function CounterSales() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/spares/counter-sales", { params: { q: q.trim() || undefined } })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q]);

  return (
    <div className="space-y-4" data-testid="counter-sales-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Counter Sales</h1>
          <p className="text-[12.5px] text-[#667085] mt-0.5">
            {rows.length} sale{rows.length === 1 ? "" : "s"} · over-the-counter parts sales.
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)}
                data-testid="counter-sales-new-btn"
                className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
          <Plus size={13} /> New Sale
        </button>
      </header>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search CS #, customer, phone, vehicle…"
               data-testid="counter-sales-search"
               className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
        {q && (
          <button onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]">
            <X size={14} />
          </button>
        )}
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="counter-sales-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <ShoppingBag size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No counter sales yet</div>
            <p className="text-[12px] text-[#667085] mt-1">Sell parts to walk-in customers with one click.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="counter-sales-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-5 py-2.5">Number</th>
                  <th className="px-3 py-2.5">Customer</th>
                  <th className="px-3 py-2.5">Vehicle</th>
                  <th className="px-3 py-2.5 text-right">Lines</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5">Payment</th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`counter-sale-row-${r.id}`}
                      className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer"
                      onClick={() => nav(`/spare-parts/counter-sales/${r.id}`)}>
                    <td className="px-5 py-3 font-mono font-semibold text-[#1d3557]">{r.number}</td>
                    <td className="px-3 py-3 text-[#1d3557]">{r.customer_snapshot?.name || "Walk-in"}</td>
                    <td className="px-3 py-3 font-mono text-[11.5px] text-[#667085]">{r.vehicle_reg || "—"}</td>
                    <td className="px-3 py-3 text-right text-[#667085] font-mono">{(r.lines || []).length}</td>
                    <td className="px-3 py-3 text-right font-mono font-bold">{inr(r.totals?.grand_total)}</td>
                    <td className="px-3 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-[#12B76A]/12 text-[#0F9256]">
                        {r.payment_mode}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[#667085] text-[11.5px]">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right text-[#a8dadc]"><ChevronRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {createOpen && (
        <NewSaleDialog onClose={() => setCreateOpen(false)}
                       onSaved={(sale) => { setCreateOpen(false); load(); nav(`/spare-parts/counter-sales/${sale.id}`); }} />
      )}
    </div>
  );
}

function NewSaleDialog({ onClose, onSaved }) {
  const [parts, setParts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [nextNumber, setNextNumber] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [walkin, setWalkin] = useState({ name: "", phone: "" });
  const [vehicleReg, setVehicleReg] = useState("");
  const [lines, setLines] = useState([freshLine()]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [paymentRef, setPaymentRef] = useState("");
  const [isInterstate, setIsInterstate] = useState(false);
  const [extraDiscount, setExtraDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/spares/parts", { params: { limit: 1000 } }).then((r) => setParts(r.data || []));
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    api.get("/spares/counter-sales/next-number").then((r) => setNextNumber(r.data.next_number || ""));
  }, []);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, freshLine()]);
  const removeLine = (idx) => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));

  const pickPart = (idx, part) => setLine(idx, {
    part_id: part.id, name: part.name, part_number: part.part_number || "",
    unit: part.unit || "nos", rate: part.selling_price || 0, gst_rate: part.gst_rate ?? 18,
    max_available: part.available_qty,
  });

  const totals = useMemo(() => localTotals(lines, isInterstate, extraDiscount), [lines, isInterstate, extraDiscount]);

  const canSave = lines.some((l) => l.part_id && Number(l.qty) > 0);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        customer_id: customerId || null,
        customer_snapshot: customerId ? null : (walkin.name || walkin.phone ? walkin : null),
        vehicle_reg: vehicleReg || "",
        lines: lines
          .filter((l) => l.part_id && Number(l.qty) > 0)
          .map((l) => ({
            part_id: l.part_id, name: l.name, part_number: l.part_number,
            qty: Number(l.qty), rate: Number(l.rate) || 0,
            gst_rate: Number(l.gst_rate) || 0, discount_pct: Number(l.discount_pct) || 0,
          })),
        payment_mode: paymentMode,
        payment_reference: paymentRef,
        is_interstate: isInterstate,
        extra_discount: Number(extraDiscount) || 0,
        notes,
      };
      const r = await api.post("/spares/counter-sales", payload);
      toast.success(`Sale ${r.data.number} completed`);
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create sale");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="counter-sale-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh]"
           data-testid="counter-sale-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#1d3557] text-white">
          <div>
            <h2 className="text-[16px] font-bold">Counter Sale</h2>
            <p className="text-[11.5px] text-white/70 font-mono">{nextNumber || ""}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-white/10 grid place-items-center"
                  data-testid="counter-sale-dialog-close">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Customer */}
          <section>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">Customer</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                      data-testid="counter-sale-customer"
                      className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white">
                <option value="">Walk-in / quick entry</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.phone || ""}</option>)}
              </select>
              {!customerId && (
                <>
                  <input value={walkin.name} onChange={(e) => setWalkin({ ...walkin, name: e.target.value })}
                         placeholder="Name (optional)"
                         data-testid="counter-sale-walkin-name"
                         className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
                  <input value={walkin.phone} onChange={(e) => setWalkin({ ...walkin, phone: e.target.value })}
                         placeholder="Phone (optional)"
                         data-testid="counter-sale-walkin-phone"
                         className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
                </>
              )}
            </div>
            <input value={vehicleReg} onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
                   placeholder="Vehicle reg (optional)"
                   data-testid="counter-sale-vehicle"
                   className="mt-2 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full md:w-1/2 font-mono" />
          </section>

          {/* Lines */}
          <section>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Parts</div>
              <button type="button" onClick={addLine} data-testid="counter-sale-add-line"
                      className="h-7 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                <Plus size={11} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <SaleLineRow key={idx} idx={idx} ln={ln} parts={parts}
                             onChange={(patch) => setLine(idx, patch)}
                             onPart={(p) => pickPart(idx, p)}
                             onRemove={() => removeLine(idx)}
                             removable={lines.length > 1} />
              ))}
            </div>
          </section>

          {/* Payment + totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <section className="rounded-xl border border-[#E4E7EC] p-3 space-y-3">
              <h4 className="text-[13px] font-bold text-[#1d3557]">Payment</h4>
              <div className="grid grid-cols-5 gap-1">
                {["cash", "upi", "card", "bank", "cheque"].map((m) => (
                  <button key={m} onClick={() => setPaymentMode(m)}
                          data-testid={`counter-sale-pay-${m}`}
                          className={`h-9 rounded-md text-[11.5px] font-semibold uppercase tracking-wider ${
                            paymentMode === m ? "bg-[#12B76A] text-white" : "border border-[#E4E7EC] text-[#667085]"
                          }`}>{m}</button>
                ))}
              </div>
              {paymentMode !== "cash" && (
                <input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)}
                       placeholder="Transaction reference / UTR / cheque #"
                       data-testid="counter-sale-payment-ref"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              )}
              <label className="inline-flex items-center gap-2 text-[12px] text-[#1d3557]">
                <input type="checkbox" checked={isInterstate} onChange={(e) => setIsInterstate(e.target.checked)}
                       data-testid="counter-sale-interstate"
                       className="w-4 h-4 accent-[#e63946]" />
                Inter-state (IGST)
              </label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Notes / remarks"
                        className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
            </section>
            <section className="rounded-xl border border-[#E4E7EC] p-3" data-testid="counter-sale-totals">
              <h4 className="text-[13px] font-bold text-[#1d3557] mb-2">Summary</h4>
              <SumRow label="Subtotal" value={inr(totals.subtotal)} />
              <SumRow label="Taxable" value={inr(totals.taxable)} muted />
              {isInterstate ? (
                <SumRow label="IGST" value={inr(totals.igst)} muted />
              ) : (
                <>
                  <SumRow label="CGST" value={inr(totals.cgst)} muted />
                  <SumRow label="SGST" value={inr(totals.sgst)} muted />
                </>
              )}
              <div className="flex items-center gap-2 py-1.5">
                <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] flex-1">Extra discount</span>
                <input type="number" value={extraDiscount}
                       onChange={(e) => setExtraDiscount(e.target.value)}
                       data-testid="counter-sale-extra-discount"
                       className="w-20 h-8 px-2 rounded-md border border-[#E4E7EC] text-[12px] font-mono text-right" />
              </div>
              <div className="my-1.5 border-t border-[#F1FAEE]" />
              <SumRow label="Grand total" value={inr(totals.grand_total)} big />
            </section>
          </div>

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="counter-sale-error">
              {error}
            </div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={save} disabled={!canSave || saving}
                  data-testid="counter-sale-submit"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Completing…" : "Complete sale"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function SaleLineRow({ idx, ln, parts, onChange, onPart, onRemove, removable }) {
  const [q, setQ] = useState(ln.name || "");
  const [open, setOpen] = useState(false);
  const results = q
    ? parts.filter((p) =>
        (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (p.part_number || "").toLowerCase().includes(q.toLowerCase()))
        .slice(0, 8)
    : parts.slice(0, 8);

  return (
    <div className="rounded-lg border border-[#E4E7EC] p-3" data-testid={`counter-sale-line-${idx}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-5 relative">
          <input value={ln.part_id ? ln.name : q}
                 onFocus={() => setOpen(true)}
                 onBlur={() => setTimeout(() => setOpen(false), 200)}
                 onChange={(e) => { setQ(e.target.value); onChange({ part_id: null, name: e.target.value }); }}
                 placeholder="Search part…"
                 data-testid={`counter-sale-line-${idx}-part`}
                 className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
          {ln.part_id && (
            <div className="text-[10.5px] font-mono text-[#0F9256] mt-0.5">
              {ln.part_number} · in stock: {ln.max_available ?? "—"}
            </div>
          )}
          {open && results.length > 0 && (
            <ul className="absolute z-10 mt-1 bg-white border border-[#E4E7EC] rounded-md shadow-lg w-full max-h-52 overflow-y-auto text-[12.5px]">
              {results.map((p) => (
                <li key={p.id}>
                  <button type="button"
                          onMouseDown={(e) => { e.preventDefault(); onPart(p); setOpen(false); setQ(p.name); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-[#F1FAEE]">
                    <div className="font-semibold text-[#1d3557]">{p.name}</div>
                    <div className="text-[10.5px] text-[#667085]">
                      {p.part_number || ""} · avail {p.available_qty} · ₹{p.selling_price || 0}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <input type="number" min={0} value={ln.qty}
               onChange={(e) => onChange({ qty: Number(e.target.value) })}
               placeholder="Qty"
               data-testid={`counter-sale-line-${idx}-qty`}
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.rate}
               onChange={(e) => onChange({ rate: Number(e.target.value) })}
               placeholder="Rate"
               data-testid={`counter-sale-line-${idx}-rate`}
               className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.gst_rate}
               onChange={(e) => onChange({ gst_rate: Number(e.target.value) })}
               placeholder="GST%"
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} max={100} value={ln.discount_pct}
               onChange={(e) => onChange({ discount_pct: Number(e.target.value) })}
               placeholder="Disc%"
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <div className="md:col-span-1 h-9 grid place-items-center font-mono tabular-nums font-bold text-[13px]">
          {inr((Number(ln.qty) || 0) * (Number(ln.rate) || 0) * (1 - (Number(ln.discount_pct) || 0) / 100))}
        </div>
        <button type="button" onClick={onRemove} disabled={!removable}
                data-testid={`counter-sale-line-${idx}-remove`}
                className="md:col-span-1 w-full h-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#a8dadc] hover:text-[#e63946] hover:border-[#e63946]/40 disabled:opacity-30">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function SumRow({ label, value, muted, big }) {
  return (
    <div className={`flex items-center justify-between py-1 ${big ? "text-[15px] font-bold" : "text-[12.5px]"}`}>
      <span className={muted ? "text-[#667085]" : "text-[#1d3557]"}>{label}</span>
      <span className={`font-mono tabular-nums ${muted ? "text-[#667085]" : "text-[#1d3557]"}`}>{value}</span>
    </div>
  );
}

function freshLine() {
  return { part_id: null, name: "", part_number: "", qty: 1, rate: 0, gst_rate: 18, discount_pct: 0, max_available: null };
}

function localTotals(lines, is_interstate, extra_discount) {
  let subtotal = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0;
  for (const ln of lines) {
    const qty = Number(ln.qty) || 0, rate = Number(ln.rate) || 0;
    const gross = qty * rate;
    const tx = gross * (1 - (Number(ln.discount_pct) || 0) / 100);
    const gr = Number(ln.gst_rate) || 0;
    if (is_interstate) igst += tx * gr / 100;
    else { cgst += tx * gr / 200; sgst += tx * gr / 200; }
    subtotal += gross; taxable += tx;
  }
  const grand = taxable + cgst + sgst + igst - (Number(extra_discount) || 0);
  return { subtotal, taxable, cgst, sgst, igst, grand_total: grand };
}
